const planScalarFields = [
  "name",
  "description",
  "currency",
  "status",
  "region",
  "price_type",
  "trial_period_days",
  "modify_orders",
  "unit_price_type",
  "plan_type",
];

const unitFields = [
  "StartRange",
  "EndRange",
  "Steps",
  "Marketplace_price",
  "Industry_price",
  "Vendor_price",
  "Min_price",
  "Max_price",
  "DefaultValue",
  "hsn_code",
];

const featureMetaFields = ["name", "data"];

const toDisplay = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (value === null || value === undefined || value === "") return "--";
  return String(value);
};

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => normalizeValue(v));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeValue(value[key]);
        return acc;
      }, {});
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (value === "true") return true;
  if (value === "false") return false;
  return value ?? null;
};

const valuesEqual = (a, b) =>
  JSON.stringify(normalizeValue(a)) === JSON.stringify(normalizeValue(b));

const mapById = (list = []) =>
  list.reduce((acc, item) => {
    if (item?.id !== undefined && item?.id !== null) {
      acc[item.id] = item;
    }
    return acc;
  }, {});

const mapFeaturesByKey = (featureList = []) =>
  featureList.reduce((acc, feature) => {
    if (feature?.key) {
      acc[feature.key] = feature;
    }
    return acc;
  }, {});

export const parseChangeJson = (rawValue) => {
  if (!rawValue) return {};
  if (typeof rawValue === "string") {
    try {
      return JSON.parse(rawValue);
    } catch (_error) {
      return {};
    }
  }
  return rawValue;
};

export const buildPlanChangeMap = (changeJson = {}) => {
  const beforePlans = changeJson?.plans?.before || [];
  const afterPlans = changeJson?.plans?.after || [];

  const beforeById = mapById(beforePlans);
  const changeMap = {};

  afterPlans.forEach((afterPlan) => {
    const planId = afterPlan?.id;
    if (planId === undefined || planId === null) return;

    const beforePlan = beforeById[planId];
    const fieldChanges = new Set();
    const fieldDiffs = {};
    const featureChanges = {};
    const unitChanges = {};

    if (!beforePlan) {
      fieldChanges.add("__new_plan");
      changeMap[planId] = {
        isChanged: true,
        fieldChanges,
        fieldDiffs,
        featureChanges,
        unitChanges,
      };
      return;
    }

    planScalarFields.forEach((fieldName) => {
      if (!valuesEqual(beforePlan[fieldName], afterPlan[fieldName])) {
        fieldChanges.add(fieldName);
        fieldDiffs[fieldName] = {
          before: toDisplay(beforePlan[fieldName]),
          after: toDisplay(afterPlan[fieldName]),
        };
      }
    });

    if (!valuesEqual(beforePlan.tags, afterPlan.tags)) {
      fieldChanges.add("tags");
      fieldDiffs.tags = {
        before: toDisplay(beforePlan.tags),
        after: toDisplay(afterPlan.tags),
      };
    }

    if (!valuesEqual(beforePlan.plan_highlight, afterPlan.plan_highlight)) {
      fieldChanges.add("plan_highlight");
      fieldDiffs.plan_highlight = {
        before: toDisplay(beforePlan.plan_highlight),
        after: toDisplay(afterPlan.plan_highlight),
      };
    }

    const beforeUnitsById = mapById(beforePlan.unit_ids || []);
    (afterPlan.unit_ids || []).forEach((afterUnit) => {
      const unitId = afterUnit?.id;
      if (unitId === undefined || unitId === null) return;

      const beforeUnit = beforeUnitsById[unitId];
      const changedUnitFields = {};

      if (!beforeUnit) {
        changedUnitFields.__new_unit = {
          before: "--",
          after: "New unit added",
        };
      } else {
        unitFields.forEach((fieldName) => {
          if (!valuesEqual(beforeUnit[fieldName], afterUnit[fieldName])) {
            changedUnitFields[fieldName] = {
              before: toDisplay(beforeUnit[fieldName]),
              after: toDisplay(afterUnit[fieldName]),
            };
          }
        });
      }

      if (Object.keys(changedUnitFields).length > 0) {
        unitChanges[unitId] = changedUnitFields;
      }
    });

    if (Object.keys(unitChanges).length > 0) {
      fieldChanges.add("unit_ids");
    }

    const beforeFeaturesByKey = mapFeaturesByKey(beforePlan.features || []);
    (afterPlan.features || []).forEach((afterFeature) => {
      const featureKey = afterFeature?.key;
      if (!featureKey) return;

      const beforeFeature = beforeFeaturesByKey[featureKey];
      const changedIndices = {};

      if (!beforeFeature) {
        (afterFeature?.data || []).forEach((value, index) => {
          changedIndices[index] = {
            before: "--",
            after: toDisplay(value),
          };
        });
      } else {
        const maxLen = Math.max(
          afterFeature?.data?.length || 0,
          beforeFeature?.data?.length || 0,
        );
        for (let index = 0; index < maxLen; index += 1) {
          if (!valuesEqual(beforeFeature?.data?.[index], afterFeature?.data?.[index])) {
            changedIndices[index] = {
              before: toDisplay(beforeFeature?.data?.[index]),
              after: toDisplay(afterFeature?.data?.[index]),
            };
          }
        }
      }

      if (Object.keys(changedIndices).length > 0) {
        featureChanges[featureKey] = changedIndices;
      }
    });

    if (Object.keys(featureChanges).length > 0) {
      fieldChanges.add("features");
    }

    changeMap[planId] = {
      isChanged: fieldChanges.size > 0,
      fieldChanges,
      fieldDiffs,
      featureChanges,
      unitChanges,
    };
  });

  return changeMap;
};

const getTypeKey = (type) =>
  `${type?.skuId ?? type?.id ?? type?.typeId ?? type?.name ?? ""}::${type?.template ?? ""}`;

const getIntervalKey = (interval) =>
  `${interval?.typeId ?? ""}::${interval?.n_interval ?? ""}::${interval?.interval ?? ""}`;

export const buildTypeChangeMap = (changeJson = {}) => {
  const before = changeJson?.planType?.before || [];
  const after = changeJson?.planType?.after || [];
  const beforeByKey = before.reduce((acc, item) => {
    acc[getTypeKey(item)] = item;
    return acc;
  }, {});
  return after.reduce((acc, item, index) => {
    const key = getTypeKey(item);
    const prev = beforeByKey[key] || before[index];
    const changed = !valuesEqual(prev, item);
    acc[key] = {
      changed,
      before: prev ? toDisplay(prev.name) : "--",
      after: toDisplay(item?.name),
    };
    return acc;
  }, {});
};

export const buildIntervalChangeMap = (changeJson = {}) => {
  const before = changeJson?.intervals?.before || [];
  const after = changeJson?.intervals?.after || [];
  const beforeByKey = before.reduce((acc, item) => {
    acc[getIntervalKey(item)] = item;
    return acc;
  }, {});
  return after.reduce((acc, item, index) => {
    const key = getIntervalKey(item);
    const prev = beforeByKey[key] || before[index];
    const changed = !valuesEqual(prev, item);
    acc[key] = {
      changed,
      before: prev
        ? `${toDisplay(prev?.n_interval)} ${toDisplay(prev?.interval)}`
        : "--",
      after: `${toDisplay(item?.n_interval)} ${toDisplay(item?.interval)}`,
    };
    return acc;
  }, {});
};

export const buildFeatureMetaChangeMap = (changeJson = {}) => {
  const before = changeJson?.features?.before || [];
  const after = changeJson?.features?.after || [];
  const beforeByKey = mapFeaturesByKey(before);
  const changeMap = {};

  after.forEach((feature) => {
    const key = feature?.key;
    if (!key) return;
    const prev = beforeByKey[key];
    const fieldDiffs = {};

    featureMetaFields.forEach((fieldName) => {
      if (!valuesEqual(prev?.[fieldName], feature?.[fieldName])) {
        fieldDiffs[fieldName] = {
          before: toDisplay(prev?.[fieldName]),
          after: toDisplay(feature?.[fieldName]),
        };
      }
    });

    const valueDiffs = {};
    const maxLen = Math.max(prev?.data?.length || 0, feature?.data?.length || 0);
    for (let index = 0; index < maxLen; index += 1) {
      if (!valuesEqual(prev?.data?.[index], feature?.data?.[index])) {
        valueDiffs[index] = {
          before: toDisplay(prev?.data?.[index]),
          after: toDisplay(feature?.data?.[index]),
        };
      }
    }

    changeMap[key] = {
      changed: Object.keys(fieldDiffs).length > 0 || Object.keys(valueDiffs).length > 0,
      fieldDiffs,
      valueDiffs,
    };
  });

  return changeMap;
};
